/**
 * Shown when a section save could not be merged with someone else's simultaneous save.
 * Both versions were kept on the server: theirs stays current, yours is saved as a separate
 * version. The writer chooses which one should be current (or opens the version history).
 */
import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Typography,
} from '@mui/material';
import { CallMerge as MergeIcon } from '@mui/icons-material';
import { markdownToPlainText } from 'shared/utils/richTextMarkdown';

const text = (lines = []) => markdownToPlainText(lines.join('\n')).trim() || '(removed)';

export default function SaveConflictDialog({ conflict, onClose, onUseMine, onShowHistory }) {
  const [busy, setBusy] = useState(false);
  if (!conflict) return null;
  const who = conflict.other_authors?.length ? conflict.other_authors.join(', ') : 'Another writer';
  const n = conflict.conflicts?.length || 0;
  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="md" fullWidth sx={{ zIndex: 3200 }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MergeIcon color="warning" />
        Simultaneous changes to “{conflict.section?.title}”
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          {who} saved this section while you were editing it, and changed {n === 1 ? 'a paragraph' : `${n} paragraphs`} that
          you changed too. Nothing was lost: their version (v{conflict.current?.version}) is current, and yours is saved
          as v{conflict.mine?.version} in the version history.
        </Alert>
        <Stack spacing={2}>
          {(conflict.conflicts || []).map((c, i) => (
            <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Their version (current)</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{text(c.theirs)}</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'warning.light' }}>
                <Typography variant="caption" color="text.secondary">Your version</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{text(c.mine)}</Typography>
              </Paper>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onShowHistory} disabled={busy} sx={{ mr: 'auto' }}>Open version history</Button>
        <Button onClick={onClose} disabled={busy}>Keep theirs</Button>
        <Button
          variant="contained" color="warning" disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
          onClick={async () => { setBusy(true); try { await onUseMine(); } finally { setBusy(false); } }}
        >
          Use mine
        </Button>
      </DialogActions>
    </Dialog>
  );
}
